import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ds';
import { StatusBadge } from '@/components/ds';
import { formatDate } from '@/lib/utils';
import {
  ShieldCheck, AlertTriangle, Clock, User, Building2,
  CheckCircle2, XCircle, Activity,
} from 'lucide-react';

/**
 * AgentSnapshot — condensed operational status block at the top of the
 * Command Center. Answers "is this agent healthy and moving?" in one glance.
 *
 * Five data cells: Contract Status · Certifications · Agency · Portal Access
 * · Last Activity. Everything else drills into the specific block below.
 */
const Cell = ({ label, value, tone = 'neutral', icon: Icon, hint, link }) => {
  const tones = {
    neutral: { fg: 'var(--kr-navy-800)', bg: '#f6f7fa' },
    success: { fg: '#15794a', bg: '#e9f6ef' },
    warning: { fg: 'var(--kr-gold-600)', bg: 'var(--kr-gold-50)' },
    danger:  { fg: '#a11b1b', bg: '#fdecec' },
    info:    { fg: '#1a4e88', bg: '#e6f0fb' },
  };
  const t = tones[tone] || tones.neutral;
  const inner = (
    <div className="flex items-start gap-3">
      {Icon && (
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: t.bg, color: t.fg }}
        >
          <Icon size={17} />
        </div>
      )}
      <div className="min-w-0">
        <div
          className="text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--kr-subtle)' }}
        >
          {label}
        </div>
        <div
          className="text-sm font-semibold mt-0.5 truncate"
          style={{ color: 'var(--kr-navy-900)' }}
        >
          {value ?? '—'}
        </div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
  return link ? (
    <Link to={link} className="hover:opacity-80 transition-opacity">{inner}</Link>
  ) : (
    inner
  );
};

const contractStatusFromAppointments = (appointments = []) => {
  const active = appointments.filter((a) => a.appointment_status === 'Approved' && a.writing_number).length;
  const pending = appointments.filter((a) => a.appointment_status === 'Pending').length;
  if (active > 0) return { label: `${active} Active`, tone: 'success', icon: ShieldCheck, hint: pending ? `${pending} pending` : null };
  if (pending > 0) return { label: `${pending} Pending`, tone: 'warning', icon: Clock };
  return { label: 'No contracts', tone: 'neutral', icon: ShieldCheck };
};

export const AgentSnapshot = ({ agent, agency, hierarchy, appointments, requests = [], linkedUser, complianceStatus }) => {
  const contract = contractStatusFromAppointments(appointments);
  const openRequests = requests.filter((r) => !['Approved', 'Declined', 'Terminated', 'Archived'].includes(r.current_status)).length;
  const lastActivity = agent?.updated_at || agent?.created_at;

  const complianceLabel = complianceStatus?.compliant
    ? 'Compliant'
    : complianceStatus?.missing_count
      ? `${complianceStatus.missing_count} missing`
      : 'Not assessed';
  const complianceTone = complianceStatus?.compliant ? 'success' : complianceStatus?.missing_count ? 'warning' : 'neutral';

  return (
    <Card testId="agent-snapshot">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        <Cell
          label="Contract Status"
          value={contract.label}
          tone={contract.tone}
          icon={contract.icon}
          hint={openRequests ? `${openRequests} request${openRequests === 1 ? '' : 's'} in flight` : null}
        />
        <Cell
          label="Certifications"
          value={complianceLabel}
          tone={complianceTone}
          icon={complianceTone === 'success' ? CheckCircle2 : AlertTriangle}
        />
        <Cell
          label="Agency"
          value={agency?.agency_name || hierarchy?.agency_name || '—'}
          icon={Building2}
          hint={hierarchy?.agency_depth != null ? `Level ${hierarchy.agency_depth}` : null}
          link={agency?.agency_id ? `/agencies/${agency.agency_id}` : null}
        />
        <Cell
          label="Portal Access"
          value={linkedUser?.status === 'active' ? 'Active' : linkedUser ? 'Disabled' : 'Not invited'}
          tone={linkedUser?.status === 'active' ? 'success' : linkedUser ? 'danger' : 'neutral'}
          icon={linkedUser?.status === 'active' ? User : XCircle}
        />
        <Cell
          label="Last Activity"
          value={lastActivity ? formatDate(lastActivity) : '—'}
          tone="info"
          icon={Activity}
        />
      </div>
      {agent?.agent_status !== 'active' && (
        <div
          className="mt-4 pt-4 border-t flex items-center justify-between"
          style={{ borderColor: 'var(--kr-border)' }}
        >
          <div className="text-xs" style={{ color: 'var(--kr-muted)' }}>
            Agent lifecycle stage: <StatusBadge variant={agent?.agent_status === 'onboarding' ? 'warning' : 'neutral'}>{agent?.agent_status}</StatusBadge>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AgentSnapshot;
